import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowRight, ArrowLeft, Phone, Upload, CheckCircle2 } from 'lucide-react';

export const GapAnalysisBlog = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>How GAP Analysis Saves You from Dossier Rejections | TheApostrophe</title>
        <meta name="description" content="Learn how regulatory gap analysis ensures your dossier is complete, consistent, and regulator-ready before submission." />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 mb-8 transition-colors duration-200 hover:opacity-70" style={{ color: '#606090' }}>
            <ArrowLeft className="h-4 w-4" />
            Back to Blog
          </Link>
          <span className="inline-block px-4 py-2 rounded-full text-sm font-semibold text-white mb-6" style={{ backgroundColor: '#606090' }}>
            Regulatory Strategy
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
            How GAP Analysis Saves You from Dossier Rejections
          </h1>
          <p className="text-xl" style={{ color: '#708080' }}>
            Jan 2025 · 8 min read
          </p>
        </div>
      </section>

      {/* Blog Content */}
      <section className="pb-24 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none" style={{ color: '#303040' }}>
              <p className="text-xl leading-relaxed mb-8" style={{ color: '#708080' }}>
                Dossier rejections rarely happen because a product is "bad." They happen because the submission wasn't complete, consistent, or aligned with the Ministry of Health's expectations. Missing COAs. Outdated stability data. Incorrect artwork. DMFs that don't match the formulation. GMP certificates not aligned with the production site. These are small details — but they cause major delays, rework, and in many cases, outright rejection.
              </p>

              <h2 className="text-3xl font-bold mb-4 mt-12" style={{ color: '#606090' }}>
                Why GAP Analysis Is Essential for Successful Dossier Submission
              </h2>
              <p className="mb-6">
                A dossier is only as strong as the documentation behind it. If even one part does not meet the MoH checklist, the entire submission becomes vulnerable. A structured dossier gap check examines every technical and administrative document against country-specific requirements.
              </p>

              <h3 className="text-2xl font-bold mb-4 mt-10" style={{ color: '#303040' }}>
                1. Ensures COAs and Specifications Match MoH Requirements
              </h3>
              <p className="mb-4">Many MoH authorities reject dossiers simply because:</p>
              <ul className="mb-6 space-y-2">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#606090' }} />
                  <span>A COA is missing a test parameter</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#606090' }} />
                  <span>Methods don't match pharmacopeial standards</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#606090' }} />
                  <span>Data is older than the permitted validity period</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#606090' }} />
                  <span>API and finished-product COAs do not correlate</span>
                </li>
              </ul>

              <h3 className="text-2xl font-bold mb-4 mt-10" style={{ color: '#303040' }}>
                2. Verifies Stability Data Is Complete and Valid
              </h3>
              <p className="mb-6">
                Stability studies are one of the most common reasons dossiers are queried. A regulatory gap analysis confirms that your stability file is recent, complete, conducted under the correct ICH climatic zone, and consistent with your manufacturing location and packaging.
              </p>

              <h3 className="text-2xl font-bold mb-4 mt-10" style={{ color: '#303040' }}>
                3. Checks API/DMF Documentation for Consistency
              </h3>
              <p className="mb-6">
                Regulators closely examine the API source, manufacturing process, and DMF to ensure ingredient integrity. Gap analysis catches issues early by confirming current DMF version, complete annexures, and active LOA.
              </p>

              <h3 className="text-2xl font-bold mb-4 mt-10" style={{ color: '#303040' }}>
                4. Validates GMP, CoPP, and Manufacturing Licenses
              </h3>
              <p className="mb-6">
                A dossier gap check ensures that GMP and CoPP fall within validity windows, manufacturing site names match across all documents, and all compliance certifications are consistent with product files.
              </p>

              <h3 className="text-2xl font-bold mb-4 mt-10" style={{ color: '#303040' }}>
                5. Identifies Mistakes in Artwork, Labels, and Inserts
              </h3>
              <p className="mb-6">
                Incorrect artwork is one of the costliest causes of rejection because it often requires redesign, re-translation, and full resubmission. Gap analysis audits artwork against country-specific labeling rules.
              </p>

              <h2 className="text-3xl font-bold mb-6 mt-12" style={{ color: '#606090' }}>
                Final Takeaway
              </h2>
              <p className="mb-6">
                A dossier doesn't get rejected because the product is weak — it gets rejected because the documents are incomplete. Regulatory gap analysis is the safeguard that prevents these failures. For exporters aiming for smooth market entry, fewer questions, and faster approvals, gap analysis is not optional — it is the foundation of successful dossier submission.
              </p>
            </div>
          </Card>

          {/* CTAs */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <Link to="/success-packs/gap-analysis-pack">
              <Card className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0" style={{ background: 'linear-gradient(135deg, #60609015 0%, #60609005 100%)' }}>
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#606090' }}>
                  GAP Analysis Pack
                </h3>
                <p className="mb-4" style={{ color: '#708080' }}>
                  Get a comprehensive module-wise document gap review for your dossier.
                </p>
                <span className="inline-flex items-center font-semibold" style={{ color: '#606090' }}>
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </span>
              </Card>
            </Link>
            <Link to="/contact">
              <Card className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0" style={{ background: 'linear-gradient(135deg, #A060B015 0%, #A060B005 100%)' }}>
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#A060B0' }}>
                  Book a Consultation
                </h3>
                <p className="mb-4" style={{ color: '#708080' }}>
                  Discuss your dossier challenges with our regulatory experts.
                </p>
                <span className="inline-flex items-center font-semibold" style={{ color: '#A060B0' }}>
                  Get in Touch <ArrowRight className="ml-2 h-4 w-4" />
                </span>
              </Card>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
